import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  DocumentSnapshot,
  QueryConstraint,
  Timestamp,
  writeBatch,
  increment,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';
import { 
  User, 
  VSLA, 
  Membership, 
  Cycle, 
  Transaction, 
  Loan, 
  Meeting, 
  Project,
  SocialFund,
  Notification,
  Document,
  AuditLog,
  COLLECTIONS 
} from './firestore-schema';

// Generic API response interface
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Pagination interface
interface PaginationOptions {
  limit?: number;
  lastDoc?: DocumentSnapshot;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
}

interface PaginatedResponse<T> {
  data: T[];
  hasMore: boolean;
  lastDoc?: DocumentSnapshot;
  total?: number;
}

// Base API service class
export class ApiService {
  /**
   * Generic function to get all documents from a collection
   */
  static async getAll<T>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<ApiResponse<T[]>> {
    try {
      const collectionRef = collection(db, collectionName);
      const q = query(collectionRef, ...constraints);
      const querySnapshot = await getDocs(q);
      
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
      
      return {
        success: true,
        data,
        message: 'Data retrieved successfully'
      };
    } catch (error) {
      console.error(`Error getting ${collectionName}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Generic function to get a single document by ID
   */
  static async getById<T>(
    collectionName: string,
    id: string
  ): Promise<ApiResponse<T>> {
    try {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return {
          success: false,
          error: 'Document not found'
        };
      }
      
      const data = {
        id: docSnap.id,
        ...docSnap.data()
      } as T;
      
      return {
        success: true,
        data,
        message: 'Document retrieved successfully'
      };
    } catch (error) {
      console.error(`Error getting document from ${collectionName}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Generic function to create a new document
   */
  static async create<T>(
    collectionName: string,
    data: Omit<T, 'id'>
  ): Promise<ApiResponse<T>> {
    try {
      const collectionRef = collection(db, collectionName);
      const docRef = await addDoc(collectionRef, {
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      const newDoc = {
        id: docRef.id,
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as T;
      
      return {
        success: true,
        data: newDoc,
        message: 'Document created successfully'
      };
    } catch (error) {
      console.error(`Error creating document in ${collectionName}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Generic function to update a document
   */
  static async update<T>(
    collectionName: string,
    id: string,
    data: Partial<T>
  ): Promise<ApiResponse<T>> {
    try {
      const docRef = doc(db, collectionName, id);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date().toISOString()
      });
      
      // Get the updated document
      const updatedDoc = await this.getById<T>(collectionName, id);
      
      return {
        success: true,
        data: updatedDoc.data!,
        message: 'Document updated successfully'
      };
    } catch (error) {
      console.error(`Error updating document in ${collectionName}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Generic function to delete a document
   */
  static async delete(
    collectionName: string,
    id: string
  ): Promise<ApiResponse<void>> {
    try {
      const docRef = doc(db, collectionName, id);
      await deleteDoc(docRef);
      
      return {
        success: true,
        message: 'Document deleted successfully'
      };
    } catch (error) {
      console.error(`Error deleting document from ${collectionName}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Generic function to get paginated results
   */
  static async getPaginated<T>(
    collectionName: string,
    options: PaginationOptions = {}
  ): Promise<ApiResponse<PaginatedResponse<T>>> {
    try {
      const {
        limit: limitCount = 10,
        lastDoc,
        orderByField = 'createdAt',
        orderDirection = 'desc'
      } = options;
      
      const collectionRef = collection(db, collectionName);
      const constraints: QueryConstraint[] = [
        orderBy(orderByField, orderDirection),
        limit(limitCount + 1) // Get one extra to check if there are more
      ];
      
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }
      
      const q = query(collectionRef, ...constraints);
      const querySnapshot = await getDocs(q);
      
      const docs = querySnapshot.docs;
      const hasMore = docs.length > limitCount;
      
      // Remove the extra document if we have more
      if (hasMore) {
        docs.pop();
      }
      
      const data = docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
      
      return {
        success: true,
        data: {
          data,
          hasMore,
          lastDoc: docs.length > 0 ? docs[docs.length - 1] : undefined
        },
        message: 'Data retrieved successfully'
      };
    } catch (error) {
      console.error(`Error getting paginated ${collectionName}:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}

// User API service
export class UserService extends ApiService {
  static async getUsers(filters?: { role?: string; isActive?: boolean }): Promise<ApiResponse<User[]>> {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.role) {
      constraints.push(where('role', '==', filters.role));
    }
    
    if (filters?.isActive !== undefined) {
      constraints.push(where('isActive', '==', filters.isActive));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    return this.getAll<User>(COLLECTIONS.USERS, constraints);
  }

  static async getUserById(id: string): Promise<ApiResponse<User>> {
    return this.getById<User>(COLLECTIONS.USERS, id);
  }

  static async createUser(userData: Omit<User, 'id'>): Promise<ApiResponse<User>> {
    return this.create<User>(COLLECTIONS.USERS, userData);
  }

  static async updateUser(id: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    return this.update<User>(COLLECTIONS.USERS, id, userData);
  }

  static async deleteUser(id: string): Promise<ApiResponse<void>> {
    return this.delete(COLLECTIONS.USERS, id);
  }

  static async getUserByEmail(email: string): Promise<ApiResponse<User | null>> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where('email', '==', email),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          success: true,
          data: null,
          message: 'User not found'
        };
      }
      
      const doc = querySnapshot.docs[0];
      const userData = {
        id: doc.id,
        ...doc.data()
      } as User;
      
      return {
        success: true,
        data: userData,
        message: 'User retrieved successfully'
      };
    } catch (error) {
      console.error('Error getting user by email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async getUserByPhoneNumber(phoneNumber: string): Promise<ApiResponse<User | null>> {
    try {
      const q = query(
        collection(db, COLLECTIONS.USERS),
        where('phoneNumber', '==', phoneNumber),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          success: true,
          data: null,
          message: 'User not found'
        };
      }
      
      const doc = querySnapshot.docs[0];
      const userData = {
        id: doc.id,
        ...doc.data()
      } as User;
      
      return {
        success: true,
        data: userData,
        message: 'User retrieved successfully'
      };
    } catch (error) {
      console.error('Error getting user by phone number:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}

// VSLA API service
export class VSLAService extends ApiService {
  static async getVSLAs(filters?: { status?: string; location?: string }): Promise<ApiResponse<VSLA[]>> {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters?.location) {
      constraints.push(where('location.county', '==', filters.location));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    return this.getAll<VSLA>(COLLECTIONS.VSLAS, constraints);
  }

  static async getVSLAById(id: string): Promise<ApiResponse<VSLA>> {
    return this.getById<VSLA>(COLLECTIONS.VSLAS, id);
  }

  static async createVSLA(vslaData: Omit<VSLA, 'id'>): Promise<ApiResponse<VSLA>> {
    return this.create<VSLA>(COLLECTIONS.VSLAS, vslaData);
  }

  static async updateVSLA(id: string, vslaData: Partial<VSLA>): Promise<ApiResponse<VSLA>> {
    return this.update<VSLA>(COLLECTIONS.VSLAS, id, vslaData);
  }

  static async deleteVSLA(id: string): Promise<ApiResponse<void>> {
    return this.delete(COLLECTIONS.VSLAS, id);
  }

  static async getVSLAsByUser(userId: string): Promise<ApiResponse<VSLA[]>> {
    try {
      // First get user's memberships
      const membershipQuery = query(
        collection(db, COLLECTIONS.MEMBERSHIPS),
        where('userId', '==', userId),
        where('status', '==', 'active')
      );
      
      const membershipSnapshot = await getDocs(membershipQuery);
      const vslaIds = membershipSnapshot.docs.map(doc => doc.data().vslaId);
      
      if (vslaIds.length === 0) {
        return {
          success: true,
          data: [],
          message: 'No VSLAs found for user'
        };
      }
      
      // Get VSLAs for the user
      const vslaQuery = query(
        collection(db, COLLECTIONS.VSLAS),
        where('__name__', 'in', vslaIds)
      );
      
      const vslaSnapshot = await getDocs(vslaQuery);
      const vslas = vslaSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as VSLA[];
      
      return {
        success: true,
        data: vslas,
        message: 'VSLAs retrieved successfully'
      };
    } catch (error) {
      console.error('Error getting VSLAs by user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}

// Membership API service
export class MembershipService extends ApiService {
  static async getMemberships(filters?: { 
    vslaId?: string; 
    userId?: string; 
    status?: string; 
    role?: string 
  }): Promise<ApiResponse<Membership[]>> {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.vslaId) {
      constraints.push(where('vslaId', '==', filters.vslaId));
    }
    
    if (filters?.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    
    if (filters?.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters?.role) {
      constraints.push(where('role', '==', filters.role));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    return this.getAll<Membership>(COLLECTIONS.MEMBERSHIPS, constraints);
  }

  static async getMembershipById(id: string): Promise<ApiResponse<Membership>> {
    return this.getById<Membership>(COLLECTIONS.MEMBERSHIPS, id);
  }

  static async createMembership(membershipData: Omit<Membership, 'id'>): Promise<ApiResponse<Membership>> {
    return this.create<Membership>(COLLECTIONS.MEMBERSHIPS, membershipData);
  }

  static async updateMembership(id: string, membershipData: Partial<Membership>): Promise<ApiResponse<Membership>> {
    return this.update<Membership>(COLLECTIONS.MEMBERSHIPS, id, membershipData);
  }

  static async deleteMembership(id: string): Promise<ApiResponse<void>> {
    return this.delete(COLLECTIONS.MEMBERSHIPS, id);
  }

  static async getUserMembership(userId: string, vslaId: string): Promise<ApiResponse<Membership | null>> {
    try {
      const q = query(
        collection(db, COLLECTIONS.MEMBERSHIPS),
        where('userId', '==', userId),
        where('vslaId', '==', vslaId),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return {
          success: true,
          data: null,
          message: 'Membership not found'
        };
      }
      
      const doc = querySnapshot.docs[0];
      const membershipData = {
        id: doc.id,
        ...doc.data()
      } as Membership;
      
      return {
        success: true,
        data: membershipData,
        message: 'Membership retrieved successfully'
      };
    } catch (error) {
      console.error('Error getting user membership:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}

// Transaction API service
export class TransactionService extends ApiService {
  static async getTransactions(filters?: {
    vslaId?: string;
    userId?: string;
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<Transaction[]>> {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.vslaId) {
      constraints.push(where('vslaId', '==', filters.vslaId));
    }
    
    if (filters?.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    
    if (filters?.type) {
      constraints.push(where('type', '==', filters.type));
    }
    
    if (filters?.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters?.startDate) {
      constraints.push(where('createdAt', '>=', filters.startDate));
    }
    
    if (filters?.endDate) {
      constraints.push(where('createdAt', '<=', filters.endDate));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    return this.getAll<Transaction>(COLLECTIONS.TRANSACTIONS, constraints);
  }

  static async getTransactionById(id: string): Promise<ApiResponse<Transaction>> {
    return this.getById<Transaction>(COLLECTIONS.TRANSACTIONS, id);
  }

  static async createTransaction(transactionData: Omit<Transaction, 'id'>): Promise<ApiResponse<Transaction>> {
    return this.create<Transaction>(COLLECTIONS.TRANSACTIONS, transactionData);
  }

  static async updateTransaction(id: string, transactionData: Partial<Transaction>): Promise<ApiResponse<Transaction>> {
    return this.update<Transaction>(COLLECTIONS.TRANSACTIONS, id, transactionData);
  }

  static async deleteTransaction(id: string): Promise<ApiResponse<void>> {
    return this.delete(COLLECTIONS.TRANSACTIONS, id);
  }

  static async getUserTransactions(userId: string, vslaId?: string): Promise<ApiResponse<Transaction[]>> {
    const constraints: QueryConstraint[] = [
      where('userId', '==', userId)
    ];
    
    if (vslaId) {
      constraints.push(where('vslaId', '==', vslaId));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    return this.getAll<Transaction>(COLLECTIONS.TRANSACTIONS, constraints);
  }
}

// Audit Log service
export class AuditLogService extends ApiService {
  static async logAction(
    userId: string,
    action: string,
    resource: string,
    resourceId: string,
    oldValue?: any,
    newValue?: any,
    vslaId?: string
  ): Promise<ApiResponse<AuditLog>> {
    const auditData: Omit<AuditLog, 'id'> = {
      userId,
      action,
      resource,
      resourceId,
      oldValue,
      newValue,
      vslaId,
      ipAddress: 'unknown', // This would need to be passed from the client
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    
    return this.create<AuditLog>(COLLECTIONS.AUDIT_LOGS, auditData);
  }

  static async getAuditLogs(filters?: {
    userId?: string;
    vslaId?: string;
    resource?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<AuditLog[]>> {
    const constraints: QueryConstraint[] = [];
    
    if (filters?.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    
    if (filters?.vslaId) {
      constraints.push(where('vslaId', '==', filters.vslaId));
    }
    
    if (filters?.resource) {
      constraints.push(where('resource', '==', filters.resource));
    }
    
    if (filters?.action) {
      constraints.push(where('action', '==', filters.action));
    }
    
    if (filters?.startDate) {
      constraints.push(where('timestamp', '>=', filters.startDate));
    }
    
    if (filters?.endDate) {
      constraints.push(where('timestamp', '<=', filters.endDate));
    }
    
    constraints.push(orderBy('timestamp', 'desc'));
    
    return this.getAll<AuditLog>(COLLECTIONS.AUDIT_LOGS, constraints);
  }
}
